import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { listBookings, listContactMessages, logout, REQUEST_STATUS } from "../../services/api/adminService";
import { ADMIN_HOME_PATH, ADMIN_LOGIN_PATH } from "./AdminGuard";

export const ADMIN_MESSAGES_PATH = "/admin/messages";
export const ADMIN_CONTENT_PATH = "/admin/content";

/**
 * Owner header: section links (Bookings, Messages, Site content) and Sign out.
 *
 * The Messages link carries the number of contact messages still to do.
 * A page that loads it itself (the Messages page) passes `messageCount`
 * (null until known); when the prop is left out the header asks
 * GET /api/admin/contact-messages?handled=false.
 *
 * The Bookings link carries the number of booking requests waiting for her
 * (contract v1.3), the same way: the Bookings page passes `requestCount`;
 * elsewhere the header asks GET /api/bookings?status=scheduled&limit=1.
 *
 * @param {object} props
 * @param {number|null} [props.messageCount]
 * @param {number|null} [props.requestCount]
 */
export default function AdminHeader({ messageCount, requestCount }) {
  const navigate = useNavigate();
  const known = messageCount !== undefined;
  const [fetchedCount, setFetchedCount] = useState(null);
  const requestsKnown = requestCount !== undefined;
  const [fetchedRequests, setFetchedRequests] = useState(null);

  useEffect(() => {
    if (requestsKnown) return undefined;
    const controller = new AbortController();
    listBookings({ status: [REQUEST_STATUS], limit: 1, signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setFetchedRequests(data.total);
      })
      // Only a hint on the link, like the messages count.
      .catch(() => {});
    return () => controller.abort();
  }, [requestsKnown]);

  useEffect(() => {
    if (known) return undefined;
    const controller = new AbortController();
    listContactMessages({ handled: false, limit: 1, signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setFetchedCount(data.unhandled);
      })
      // The count is only a hint on the link; the Messages page reports
      // its own load errors. A 401 is handled by AdminGuard.
      .catch(() => {});
    return () => controller.abort();
  }, [known]);

  const count = known ? messageCount : fetchedCount;
  const requests = requestsKnown ? requestCount : fetchedRequests;

  const signOut = () => {
    logout();
    navigate(ADMIN_LOGIN_PATH, { replace: true });
  };

  const linkClass = ({ isActive }) => `admin-nav__link${isActive ? " is-active" : ""}`;

  return (
    <header className="admin-header">
      <span className="admin-brand">Healing Hues · Owner</span>
      <nav className="admin-nav" aria-label="Owner sections">
        <NavLink to={ADMIN_HOME_PATH} end className={linkClass}>
          Bookings
          {requests > 0 && (
            <span className="admin-nav__count" data-testid="requests-count">
              {requests}
              <span className="sr-only"> waiting for you</span>
            </span>
          )}
        </NavLink>
        <NavLink to={ADMIN_MESSAGES_PATH} className={linkClass}>
          Messages
          {count > 0 && (
            <span className="admin-nav__count" data-testid="messages-count">
              {count}
              <span className="sr-only"> to do</span>
            </span>
          )}
        </NavLink>
        <NavLink to={ADMIN_CONTENT_PATH} className={linkClass}>Site content</NavLink>
      </nav>
      <button type="button" className="admin-btn" onClick={signOut} data-leaves-page>Sign out</button>
    </header>
  );
}
